import React, { useState, useEffect, useContext } from "react";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisEmptyList from "../../components/controls/emptylist";
import Pagenated from "../../components/pagenated";
import DynamicIcon from "../../components/dynamicicon";
import wordicon from "../../assets/ms_word.png";
import excelicon from "../../assets/ms_excel.png";
import pdficon from "../../assets/pdf.png";

// Dynamic Report: admin picks a data set, the columns, the period and filters,
// types a title, then views/exports the result.

const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

function DynamicReport() {
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const now = new Date();
  const [datasets, setDatasets] = useState([]);
  const [form, setForm] = useState({
    title: "",
    dataset: "",
    fromDate: toYmd(new Date(now.getFullYear(), now.getMonth(), 1)),
    toDate: toYmd(now),
    divisionId: "",
    search: "",
  });
  const [selectedCols, setSelectedCols] = useState([]);
  const [applied, setApplied] = useState(null);
  const [result, setResult] = useState(null); // { title, dataset, columns, items }
  const [count, setCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const current = datasets.find((d) => d.key === form.dataset);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${Constants.apiUrl}/otherreport/dynamic/datasets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        if (data.code === 0) setDatasets(data.list || []);
        else if (data.code === -10) setIsExpired(true);
        else setErr(data.status || "Unable to load data sets");
      } catch (e) {
        console.error(e);
        setErr(Constants.serverErr);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === "dataset") {
      const ds = datasets.find((d) => d.key === value);
      setSelectedCols(ds ? ds.columns.map((c) => c.key) : []); // all ticked by default
      setResult(null);
      setCount(0);
    }
  };

  const toggleCol = (key) =>
    setSelectedCols((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const body = (f, cols) => ({
    dataset: f.dataset,
    columns: cols,
    fromDate: f.fromDate || null,
    toDate: f.toDate || null,
    divisionId: f.divisionId ? Number(f.divisionId) : null,
    search: f.search?.trim() || null,
    title: f.title?.trim() || null,
  });

  const run = async (a, pIndex, pSize) => {
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(`${Constants.apiUrl}/otherreport/dynamic/run/${pIndex}/${pSize}`, {
        method: "POST",
        body: JSON.stringify(body(a.form, a.cols)),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.code === 0) {
        setResult({ title: data.title, dataset: data.dataset, columns: data.columns, items: data.items });
        setCount(data.totalCount || 0);
      } else if (data.code === -10) {
        setIsExpired(true);
      } else {
        setErr(data.status || "Unable to generate the report");
      }
    } catch (e) {
      console.error(e);
      setErr(Constants.serverErr);
    } finally {
      setLoading(false);
    }
  };

  const onGenerate = () => {
    if (!form.dataset) return setErr("Please select a data set");
    if (selectedCols.length === 0) return setErr("Please tick at least one column");
    if (form.fromDate && form.toDate && form.fromDate > form.toDate)
      return setErr("The start date must be before the end date");
    const a = { form: { ...form }, cols: [...selectedCols] };
    setApplied(a);
    setPageIndex(0);
    run(a, 0, pageSize);
  };

  const onPagePick = (pick) => {
    const p = pick.id - 1;
    setPageIndex(p);
    if (applied) run(applied, p, pageSize);
  };

  const onPageSize = (k) => {
    setPageSize(k);
    setPageIndex(0);
    if (applied) run(applied, 0, k);
  };

  const onExport = async (doctype) => {
    if (!applied) return;
    const ext = { excel: "xlsx", msword: "docx", pdf: "pdf" }[doctype];
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(`${Constants.apiUrl}/otherreport/dynamic/export/${doctype}`, {
        method: "POST",
        body: JSON.stringify(body(applied.form, applied.cols)),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (resp.status === 409) {
        const d = await resp.json().catch(() => ({}));
        if (d.code === -10) setIsExpired(true);
        else setErr(d.status || "Access denied");
        return;
      }
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const name = (result?.title || "dynamic-report").replace(/[^a-z0-9]+/gi, "-");
      a.download = `${name}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) {
      console.error(e);
      setErr("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div className="panel">
        <div className="title">Dynamic Report</div>
        <div className="panel-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div className="input-group" style={{ flex: 2, minWidth: 260 }}>
              <label>Report Title</label>
              <div className="input-wrapper">
                <DynamicIcon iname="title" />
                <input
                  name="title"
                  value={form.title}
                  placeholder="e.g. Cases filed in the Ikeja Division - Q3 2026"
                  onChange={onChange}
                  maxLength={150}
                />
              </div>
            </div>
            <div className="select-group" style={{ flex: 1, minWidth: 220 }}>
              <label>
                Data Set <span className="required">*</span>
              </label>
              <select name="dataset" value={form.dataset} onChange={onChange}>
                <option value="">Select data set</option>
                {datasets.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div className="input-group" style={{ minWidth: 160 }}>
              <label>{current ? `${current.dateLabel} - from` : "From"}</label>
              <div className="input-wrapper">
                <input type="date" name="fromDate" value={form.fromDate} onChange={onChange} />
              </div>
            </div>
            <div className="input-group" style={{ minWidth: 160 }}>
              <label>To</label>
              <div className="input-wrapper">
                <input type="date" name="toDate" value={form.toDate} onChange={onChange} />
              </div>
            </div>
            <div className="select-group" style={{ minWidth: 180 }}>
              <label>Division</label>
              <select name="divisionId" value={form.divisionId} onChange={onChange}>
                <option value="">All Divisions</option>
                {frequentData?.divisions?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group" style={{ flex: 1, minWidth: 220 }}>
              <label>Search (optional)</label>
              <div className="input-wrapper">
                <DynamicIcon iname="search" />
                <input
                  name="search"
                  value={form.search}
                  placeholder="Suit number, name, title..."
                  onChange={onChange}
                  onKeyDown={(e) => e.key === "Enter" && onGenerate()}
                />
              </div>
            </div>
          </div>

          {current && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <strong>Columns</strong>
                <span
                  className="specialaction"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedCols(current.columns.map((c) => c.key))}
                >
                  Select all
                </span>
                <span className="specialaction" style={{ cursor: "pointer" }} onClick={() => setSelectedCols([])}>
                  Clear
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
                {current.columns.map((c) => (
                  <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={selectedCols.includes(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="panel-btn">
          <JisButton onClick={onGenerate}>Generate Report</JisButton>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {result && (
        <div className="table" style={{ marginTop: 16 }}>
          <div
            className="section-cases"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}
          >
            <span>
              {result.title}
            </span>
            {count > 0 && (
              <div className="exportdiv" style={{ display: "flex", gap: 8 }}>
                <img src={wordicon} alt="Word" title="Download Word" onClick={() => onExport("msword")} style={{ cursor: "pointer" }} />
                <img src={excelicon} alt="Excel" title="Download Excel" onClick={() => onExport("excel")} style={{ cursor: "pointer" }} />
                <img src={pdficon} alt="PDF" title="Download PDF" onClick={() => onExport("pdf")} style={{ cursor: "pointer" }} />
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, margin: "4px 0 8px" }}>
            {result.dataset} · {applied?.form.fromDate} to {applied?.form.toDate}
          </div>

          {result.items.length === 0 ? (
            <JisEmptyList title="No records match the selected criteria" />
          ) : (
            <div className="table-wrapper" style={{ overflowX: "auto" }}>
              <table className="case-table">
                <thead>
                  <tr>
                    <th className="col-id">S/N</th>
                    {result.columns.map((c) => (
                      <th key={c.key} className="col-medium">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((row, i) => (
                    <tr key={i}>
                      <td>{pageIndex * pageSize + i + 1}</td>
                      {result.columns.map((c) => (
                        <td key={c.key}>{Constants.displayDateTime(row[c.key])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {count > 0 && (
            <Pagenated onChangePageSize={onPageSize} size={pageSize} total={count} maxPage={20} onPick={onPagePick} />
          )}
        </div>
      )}

      <BackLoader open={loading} />
    </div>
  );
}

export default DynamicReport;
